import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Plus, Trash2, Save, Gavel, Tag, Users, Calendar } from "lucide-react";
import { api } from "@/App";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";

const TIPOS = [
  { value: "civil", label: "Civil" },
  { value: "penal", label: "Penal" },
  { value: "laboral", label: "Laboral" },
  { value: "familia", label: "Familia" },
  { value: "cobranza", label: "Cobranza" }
];

function EditorFases({ user, onLogout }) {
  const navigate = useNavigate();
  const [tipo, setTipo] = useState("civil");
  const [fases, setFases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadFases(tipo); }, [tipo]);

  const loadFases = async (tipoDemanda) => {
    setLoading(true);
    try {
      const res = await api.get(`/fases-plantilla?tipo_demanda=${tipoDemanda}`);
      setFases(res.data.map((f, i) => ({
        nombre: f.nombre || "",
        tipificacion: f.tipificacion || "",
        personas_concurrentes: f.personas_concurrentes || "",
        tipo_evento: f.tipo_evento || "fase",
        orden: f.orden || i + 1,
        tempId: i
      })));
    } catch { toast.error("Error cargando fases"); }
    finally { setLoading(false); }
  };

  const addFase = () => {
    setFases([...fases, {
      nombre: "",
      tipificacion: "",
      personas_concurrentes: "",
      tipo_evento: "fase",
      orden: fases.length + 1,
      tempId: Date.now()
    }]);
  };

  const removeFase = (idx) => {
    setFases(fases.filter((_, i) => i !== idx).map((f, i) => ({ ...f, orden: i + 1 })));
  };

  const updateFase = (idx, field, value) => {
    const updated = [...fases];
    updated[idx] = { ...updated[idx], [field]: value };
    setFases(updated);
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const updated = [...fases];
    [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
    setFases(updated.map((f, i) => ({ ...f, orden: i + 1 })));
  };

  const moveDown = (idx) => {
    if (idx === fases.length - 1) return;
    const updated = [...fases];
    [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
    setFases(updated.map((f, i) => ({ ...f, orden: i + 1 })));
  };

  const handleSave = async () => {
    const validFases = fases.filter(f => f.nombre.trim());
    if (validFases.length === 0) { toast.error("Agrega al menos una fase"); return; }
    setSaving(true);
    try {
      await api.post("/fases-plantilla", {
        tipo_demanda: tipo,
        fases: validFases.map(f => ({
          nombre: f.nombre.trim(),
          tipificacion: f.tipificacion || "",
          personas_concurrentes: f.personas_concurrentes || "",
          tipo_evento: f.tipo_evento || "fase"
        }))
      });
      toast.success(`Plantilla "${tipo}" guardada con ${validFases.length} fases`);
    } catch { toast.error("Error guardando plantilla"); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="editor-fases-page">
      <TopNav user={user} onLogout={onLogout} />
      <main className="container mx-auto px-6 py-6 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mb-4 text-xs" style={{ color: "var(--text-muted)" }}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Volver
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Gavel className="h-4 w-4" style={{ color: "var(--primary)" }} /> Fases y Procesos
            </CardTitle>
            <CardDescription className="text-xs">Define las etapas, tipificación, personas concurrentes y tipo de evento para cada tipo de demanda.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label className="text-xs">Tipo de Demanda</Label>
              <Select value={tipo} onValueChange={setTipo}>
                <SelectTrigger data-testid="select-tipo-fase"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPOS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="text-center py-8 text-sm" style={{ color: "var(--text-muted)" }}>Cargando...</div>
            ) : (
              <div className="space-y-3" data-testid="fases-editor-list">
                {/* Header */}
                <div className="grid grid-cols-12 gap-2 px-2 text-[10px] uppercase tracking-wider font-medium" style={{ color: "var(--text-muted)" }}>
                  <div className="col-span-1">#</div>
                  <div className="col-span-3 flex items-center gap-1"><Tag className="h-3 w-3" />Nombre fase</div>
                  <div className="col-span-2 flex items-center gap-1"><Gavel className="h-3 w-3" />Tipificación</div>
                  <div className="col-span-3 flex items-center gap-1"><Users className="h-3 w-3" />Personas concurrentes</div>
                  <div className="col-span-2 flex items-center gap-1"><Calendar className="h-3 w-3" />Tipo evento</div>
                  <div className="col-span-1"></div>
                </div>

                {fases.map((fase, idx) => (
                  <div key={fase.tempId || idx} className="grid grid-cols-12 gap-2 items-center ls-card p-2" data-testid={`fase-row-${idx}`}>
                    <div className="col-span-1 flex items-center gap-1">
                      <div className="flex flex-col gap-0">
                        <button onClick={() => moveUp(idx)} disabled={idx === 0} className="text-[10px] leading-none disabled:opacity-20 hover:text-teal-600" style={{ color: "var(--text-muted)" }}>&#9650;</button>
                        <button onClick={() => moveDown(idx)} disabled={idx === fases.length - 1} className="text-[10px] leading-none disabled:opacity-20 hover:text-teal-600" style={{ color: "var(--text-muted)" }}>&#9660;</button>
                      </div>
                      <span className="text-xs font-bold" style={{ color: "var(--text-muted)" }}>{idx + 1}</span>
                    </div>
                    <div className="col-span-3">
                      <Input
                        value={fase.nombre}
                        onChange={(e) => updateFase(idx, "nombre", e.target.value)}
                        placeholder="Nombre de la fase"
                        className="h-8 text-xs"
                        data-testid={`fase-input-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        value={fase.tipificacion}
                        onChange={(e) => updateFase(idx, "tipificacion", e.target.value)}
                        placeholder="Ej: Ordinario"
                        className="h-8 text-xs"
                        data-testid={`fase-tipificacion-${idx}`}
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        value={fase.personas_concurrentes}
                        onChange={(e) => updateFase(idx, "personas_concurrentes", e.target.value)}
                        placeholder="Ej: Juez, abogados, partes"
                        className="h-8 text-xs"
                        data-testid={`fase-personas-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Select value={fase.tipo_evento} onValueChange={(v) => updateFase(idx, "tipo_evento", v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`fase-tipo-evento-${idx}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fase">Fase procesal</SelectItem>
                          <SelectItem value="audiencia">Audiencia</SelectItem>
                          <SelectItem value="plazo">Plazo legal</SelectItem>
                          <SelectItem value="resolucion">Resolución</SelectItem>
                          <SelectItem value="notificacion">Notificación</SelectItem>
                          <SelectItem value="pericia">Pericia</SelectItem>
                          <SelectItem value="diligencia">Diligencia</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <button onClick={() => removeFase(idx)} className="p-1 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={addFase} className="flex-1 text-xs" data-testid="add-fase-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> Agregar Fase
              </Button>
              <Button onClick={handleSave} disabled={saving} className="flex-1 ls-btn-primary text-xs" data-testid="save-fases-btn">
                <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Guardando..." : "Guardar Plantilla"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

export default EditorFases;
